import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import api from "../../lib/api";
import Button from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import WorkingHours from "../../components/business/WorkingHours";
import { cn, formatDay, formatPrice } from "../../lib/utils";

const STATUS_STYLES = {
  pending: "border-amber-400 bg-amber-50 text-amber-800",
  confirmed: "border-primary bg-primary/10 text-primary",
  completed: "border-emerald-400 bg-emerald-50 text-emerald-800",
  cancelled: "border-slate-300 bg-slate-50 text-slate-400 line-through",
  no_show: "border-destructive bg-destructive/10 text-destructive",
};

const DAY_START_MINUTES = 8 * 60; // 08:00
const DAY_END_MINUTES = 20 * 60; // 20:00
const PIXELS_PER_MINUTE = 1;

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day; // Monday start
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Local calendar date — toISOString() would shift it a day east of UTC.
function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Who is bookable on a date: weekly working hours minus one-off blocked dates.
function dayAvailability(staff, date) {
  const iso = toISODate(date);
  const blocks = [];
  const windows = [];
  staff.forEach((s) => {
    const rows = s.availability ?? [];
    const block = rows.find((a) => a.is_blocked && a.blocked_date?.slice(0, 10) === iso);
    const window = rows.find((a) => !a.is_blocked && a.day_of_week === date.getDay());
    if (block) blocks.push({ staffId: s.id, id: block.id });
    else if (window) windows.push(window);
  });
  return { blocks, windows };
}

function DayAvailability({ date, staff, busy, onChange }) {
  const { blocks, windows } = dayAvailability(staff, date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const editable = date >= today && !busy;
  const linkClass = "font-medium text-primary underline disabled:opacity-50";

  if (windows.length > 0) {
    return (
      <p className="text-[11px] text-emerald-700">
        {staff.length > 1
          ? `${windows.length} of ${staff.length} available`
          : `${windows[0].start_time?.slice(0, 5)}–${windows[0].end_time?.slice(0, 5)}`}{" "}
        <button type="button" className={linkClass} disabled={!editable} onClick={() => onChange(date, false)}>
          Mark unavailable
        </button>
      </p>
    );
  }
  if (blocks.length > 0) {
    return (
      <p className="text-[11px] text-destructive">
        Unavailable{" "}
        <button type="button" className={linkClass} disabled={!editable} onClick={() => onChange(date, true)}>
          Make available
        </button>
      </p>
    );
  }
  return <p className="text-[11px] text-muted-foreground">Day off</p>;
}

function AvailabilityPanel({ staff, onSaved }) {
  const [staffId, setStaffId] = useState(staff[0]?.id);
  const member = staff.find((s) => s.id === staffId) ?? staff[0];
  if (!member) return null;

  return (
    <Card className="mb-4 p-4">
      <p className="text-sm font-semibold text-slate-900">Weekly availability</p>
      <p className="text-xs text-muted-foreground">
        Tick the days you take bookings and set the hours. For a single date, use the link under that day.
      </p>
      {staff.length > 1 && (
        <select
          value={member.id}
          onChange={(e) => setStaffId(e.target.value)}
          aria-label="Staff member"
          className="mt-2 h-9 rounded-lg border border-input bg-white px-2 text-xs outline-none focus:border-primary"
        >
          {staff.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      )}
      <WorkingHours key={member.id} staffMember={member} onSaved={onSaved} />
    </Card>
  );
}

function toMinutes(hhmmss) {
  const [h, m] = hhmmss.split(":").map(Number);
  return h * 60 + m;
}

const STATUS_LABELS = {
  pending: "Waiting for you to confirm",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "No-show",
};

const PAYMENT_LABELS = {
  unpaid: "Not paid yet — collect at the store",
  deposit_paid: "Deposit paid online",
  paid: "Paid online",
  refunded: "Refunded",
  partially_refunded: "Partly refunded",
};

// What the business can do with a booking, by where it stands.
const STATUS_ACTIONS = {
  pending: [
    { status: "confirmed", label: "Confirm" },
    { status: "cancelled", label: "Cancel booking", variant: "danger" },
  ],
  confirmed: [
    { status: "completed", label: "Mark completed", afterStart: true },
    { status: "no_show", label: "No-show", variant: "outline", afterStart: true },
    { status: "cancelled", label: "Cancel booking", variant: "danger" },
  ],
};

function AppointmentDetails({ a, busy, error, onStatus, onClose }) {
  const started = new Date(`${a.appointment_date.slice(0, 10)}T${a.start_time}`) <= new Date();
  const actions = STATUS_ACTIONS[a.status] ?? [];
  const paidOnline = Number(a.amount_paid || 0) > 0;

  const run = (action) => {
    if (action.status === "cancelled") {
      const warning = paidOnline
        ? `Cancel this booking? ${a.customer?.full_name || "The customer"} will be told straight away and refunded the ${formatPrice(a.amount_paid)} they paid.`
        : `Cancel this booking? ${a.customer?.full_name || "The customer"} will be told straight away.`;
      if (!window.confirm(warning)) return;
    }
    if (action.status === "no_show" && !window.confirm("Mark this customer as a no-show? They will be notified, and this can't be undone.")) return;
    onStatus(a, action.status);
  };

  return (
    <Card className="mb-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 text-sm">
          <p className="font-semibold text-slate-900">
            {a.customer?.full_name || "Customer"}
            {a.customer?.phone ? <span className="font-normal text-muted-foreground"> · {a.customer.phone}</span> : null}
          </p>
          <p className="text-slate-700">
            {formatDay(a.appointment_date)} · {a.start_time.slice(0, 5)}–{(a.end_time || "").slice(0, 5)}
            {a.staff?.name ? ` · ${a.staff.name}` : ""}
          </p>
          <p className="text-muted-foreground">{a.services?.map((s) => s.name_snapshot).join(", ")}</p>
          <p className="mt-1">
            <span className="font-semibold">{formatPrice(Number(a.total_amount) - Number(a.service_fee || 0))}</span>
            <span className="text-muted-foreground"> · {PAYMENT_LABELS[a.payment_status] ?? a.payment_status}</span>
          </p>
          <p className="text-muted-foreground">{STATUS_LABELS[a.status] ?? a.status}</p>
          {a.notes && <p className="mt-1 rounded-lg bg-muted p-2 text-xs text-slate-700">Note: {a.notes}</p>}
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="text-sm text-muted-foreground hover:text-foreground">
          ✕
        </button>
      </div>
      {actions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {actions.map((action) => (
            <Button
              key={action.status}
              size="sm"
              variant={action.variant}
              disabled={busy || (action.afterStart && !started)}
              title={action.afterStart && !started ? "Available once the appointment has started" : undefined}
              onClick={() => run(action)}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
      {actions.some((x) => x.afterStart) && !started && (
        <p className="mt-2 text-xs text-muted-foreground">You can mark it completed or no-show once the appointment has started.</p>
      )}
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </Card>
  );
}

function AppointmentCard({ a, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(a)}
      className={cn("block w-full rounded-lg border-l-4 p-2 text-left text-xs hover:opacity-80", STATUS_STYLES[a.status])}
    >
      <p className="font-semibold">{a.start_time.slice(0, 5)}</p>
      {a.estimated_start_time && a.estimated_start_time !== a.start_time && (
        <p className="text-[10px] opacity-70">est. {a.estimated_start_time.slice(0, 5)}</p>
      )}
      {a.queue_position && <p className="text-[10px] opacity-70">Queue #{a.queue_position}</p>}
      {a.customer_late_minutes > 0 && (
        <p className="text-[10px] font-semibold text-destructive">
          {a.customer_late_minutes}m late{a.bumped_for_lateness ? " · may bump" : ""}
        </p>
      )}
      <p className="truncate">{a.customer?.full_name}</p>
      <p className="truncate opacity-80">{a.services?.map((s) => s.name_snapshot).join(", ")}</p>
      {/* The business's share — SHUWA's booking fee is paid on top by the customer. */}
      <p className="mt-1 font-semibold">{formatPrice(Number(a.total_amount) - Number(a.service_fee || 0))}</p>
      {a.status === "pending" && <p className="mt-1 font-semibold underline">Tap to confirm</p>}
    </button>
  );
}

const HOLD_STYLE = "border-dashed border-slate-400 bg-slate-50 text-slate-600";

// A time a customer is in the middle of booking: nobody else can take it,
// and it turns into a booking or frees itself within a few minutes.
function HoldCard({ h }) {
  return (
    <div className={cn("rounded-lg border p-2 text-xs", HOLD_STYLE)}>
      <p className="font-semibold">
        {h.start_time.slice(0, 5)}–{(h.end_time || h.busy_until).slice(0, 5)}
      </p>
      <p>Being booked{h.staff?.name ? ` · ${h.staff.name}` : ""}</p>
      <p className="text-[10px] opacity-80">A customer is checking out — kept for them for a few minutes.</p>
    </div>
  );
}

function WeekView({ days, byDay, holdsByDay, staff, busy, onAvailabilityChange, onSelect }) {
  return (
    <div className="grid grid-cols-1 gap-3 overflow-x-auto sm:grid-cols-7">
      {days.map((day) => (
        <div key={day.toISOString()} className="min-w-[160px] rounded-2xl border border-border bg-white">
          <div className="border-b border-border p-2 text-center">
            <p className="text-xs text-muted-foreground">
              {day.toLocaleDateString(undefined, { weekday: "short" })}
            </p>
            <p className="text-sm font-semibold text-slate-900">{day.getDate()}</p>
            <DayAvailability date={day} staff={staff} busy={busy} onChange={onAvailabilityChange} />
          </div>
          <div className="space-y-2 p-2">
            {byDay(day).length + holdsByDay(day).length === 0 && (
              <p className="py-4 text-center text-xs text-muted-foreground">—</p>
            )}
            {byDay(day).map((a) => (
              <AppointmentCard key={a.id} a={a} onSelect={onSelect} />
            ))}
            {holdsByDay(day).map((h) => (
              <HoldCard key={h.id} h={h} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DayStaffView({ date, appointments, holds, staff, onSelect }) {
  const hourMarks = useMemo(() => {
    const marks = [];
    for (let m = DAY_START_MINUTES; m <= DAY_END_MINUTES; m += 60) marks.push(m);
    return marks;
  }, []);

  const columns = staff.length > 0 ? staff : [{ id: "unassigned", name: "Unassigned" }];

  const appointmentsForStaff = (staffId) =>
    appointments.filter((a) => (staffId === "unassigned" ? !a.staff : a.staff?.id === staffId));

  const trackHeight = DAY_END_MINUTES - DAY_START_MINUTES;

  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-white">
      <div className="flex min-w-[640px]">
        <div className="w-14 shrink-0 border-r border-border">
          <div className="h-12 border-b border-border" />
          <div className="relative" style={{ height: trackHeight }}>
            {hourMarks.map((m) => (
              <div
                key={m}
                className="absolute left-0 right-0 -translate-y-1/2 px-1 text-right text-[10px] text-muted-foreground"
                style={{ top: (m - DAY_START_MINUTES) * PIXELS_PER_MINUTE }}
              >
                {String(Math.floor(m / 60)).padStart(2, "0")}:00
              </div>
            ))}
          </div>
        </div>
        {columns.map((member) => {
          const staffAppointments = appointmentsForStaff(member.id);
          return (
            <div key={member.id} className="min-w-[180px] flex-1 border-r border-border last:border-r-0">
              <div className="flex h-12 items-center justify-center border-b border-border px-2 text-center">
                <p className="truncate text-sm font-semibold text-slate-900">{member.name}</p>
              </div>
              <div className="relative" style={{ height: trackHeight }}>
                {hourMarks.map((m) => (
                  <div
                    key={m}
                    className="absolute left-0 right-0 border-t border-dashed border-border/70"
                    style={{ top: (m - DAY_START_MINUTES) * PIXELS_PER_MINUTE }}
                  />
                ))}
                {staffAppointments.map((a) => {
                  const start = toMinutes(a.start_time);
                  const end = toMinutes(a.estimated_end_time || a.end_time || a.start_time) || start + 30;
                  const top = Math.max(0, (start - DAY_START_MINUTES) * PIXELS_PER_MINUTE);
                  const height = Math.max(28, (end - start) * PIXELS_PER_MINUTE);
                  return (
                    <button
                      type="button"
                      key={a.id}
                      onClick={() => onSelect(a)}
                      className={cn(
                        "absolute left-1 right-1 overflow-hidden rounded-lg border-l-4 p-1.5 text-left text-[11px] shadow-sm hover:opacity-80",
                        STATUS_STYLES[a.status]
                      )}
                      style={{ top, height }}
                      title={`${a.start_time.slice(0, 5)} · ${a.customer?.full_name}`}
                    >
                      <p className="truncate font-semibold">
                        {a.start_time.slice(0, 5)} · {a.customer?.full_name}
                      </p>
                      <p className="truncate opacity-80">{a.services?.map((s) => s.name_snapshot).join(", ")}</p>
                    </button>
                  );
                })}
                {holds
                  .filter((h) => h.staff_id === member.id)
                  .map((h) => {
                    const start = toMinutes(h.start_time);
                    const end = toMinutes(h.end_time || h.busy_until);
                    return (
                      <div
                        key={h.id}
                        className={cn("absolute left-1 right-1 overflow-hidden rounded-lg border p-1.5 text-[11px]", HOLD_STYLE)}
                        style={{
                          top: Math.max(0, (start - DAY_START_MINUTES) * PIXELS_PER_MINUTE),
                          height: Math.max(28, (end - start) * PIXELS_PER_MINUTE),
                        }}
                        title="A customer is checking out — kept for them for a few minutes."
                      >
                        <p className="truncate font-semibold">{h.start_time.slice(0, 5)} · Being booked</p>
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Calendar() {
  const [view, setView] = useState("week");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [appointments, setAppointments] = useState([]);
  const [holds, setHolds] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAvailability, setShowAvailability] = useState(false);
  const [availabilityBusy, setAvailabilityBusy] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [staffLoaded, setStaffLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState(null);

  const selected = appointments.find((a) => a.id === selectedId);
  const pendingCount = appointments.filter((a) => a.status === "pending").length;
  // Customers can only book staff who have working hours set.
  const staffWithoutHours = staff.filter((s) => !(s.availability ?? []).some((a) => !a.is_blocked));

  const select = (a) => {
    setSelectedId(a.id);
    setStatusError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateStatus = async (a, status) => {
    setStatusBusy(true);
    setStatusError(null);
    try {
      const { data } = await api.patch(`/owner/appointments/${a.id}/status`, { status });
      setAppointments((list) => list.map((x) => (x.id === a.id ? { ...x, ...data } : x)));
    } catch (e) {
      setStatusError(e?.response?.data?.message || "Could not update this booking.");
    } finally {
      setStatusBusy(false);
    }
  };

  const days = useMemo(
    () =>
      Array.from({ length: 7 }).map((_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
      }),
    [weekStart]
  );

  const rangeStart = view === "day" ? selectedDay : days[0];
  const rangeEnd = view === "day" ? selectedDay : days[6];

  const loadStaff = () =>
    api
      .get("/owner/staff")
      .then(({ data }) => setStaff((data || []).filter((s) => s.is_active !== false)))
      .catch(() => setStaff([]))
      .finally(() => setStaffLoaded(true));

  useEffect(() => {
    loadStaff();
  }, []);

  // Blocks (or reopens) one date for the whole team, on top of the weekly hours.
  const setDayAvailable = async (date, available) => {
    const { blocks } = dayAvailability(staff, date);
    setAvailabilityBusy(true);
    setAvailabilityError(null);
    try {
      if (available) {
        await Promise.all(blocks.map((b) => api.delete(`/owner/staff/${b.staffId}/time-off/${b.id}`)));
      } else {
        await Promise.all(
          staff
            .filter((s) => !blocks.some((b) => b.staffId === s.id))
            .map((s) => api.post(`/owner/staff/${s.id}/time-off`, { blocked_date: toISODate(date) }))
        );
      }
    } catch (e) {
      setAvailabilityError(e?.response?.data?.message || "Could not update availability.");
    } finally {
      await loadStaff();
      setAvailabilityBusy(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    const from = toISODate(rangeStart);
    const to = toISODate(rangeEnd);
    let stale = false;
    const load = () =>
      api
        .get("/owner/appointments", { params: { from, to } })
        .then(({ data }) => {
          if (stale) return;
          setAppointments(data.appointments || []);
          setHolds(data.holds || []);
        })
        .catch(() => {});
    load().finally(() => !stale && setLoading(false));
    // Reloaded every half minute, so new bookings and times being booked show up on their own.
    const timer = setInterval(load, 30000);
    return () => {
      stale = true;
      clearInterval(timer);
    };
  }, [rangeStart, rangeEnd]);

  const onDay = (list, date) =>
    list
      .filter((x) => x.appointment_date.slice(0, 10) === toISODate(date))
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  const byDay = (date) => onDay(appointments, date);
  const holdsByDay = (date) => onDay(holds, date);

  const shiftWeek = (deltaWeeks) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + deltaWeeks * 7);
    setWeekStart(d);
  };

  const shiftDay = (deltaDays) => {
    const d = new Date(selectedDay);
    d.setDate(d.getDate() + deltaDays);
    setSelectedDay(d);
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Calendar</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAvailability((v) => !v)}>
            <Clock className="h-4 w-4" /> Availability
          </Button>
          <div className="flex rounded-xl border border-border bg-white p-0.5">
            <button
              type="button"
              onClick={() => setView("day")}
              className={cn(
                "rounded-lg px-3 py-1.5 text-xs font-semibold",
                view === "day" ? "bg-primary/10 text-primary" : "text-slate-600"
              )}
            >
              Day
            </button>
            <button
              type="button"
              onClick={() => setView("week")}
              className={cn(
                "rounded-lg px-3 py-1.5 text-xs font-semibold",
                view === "week" ? "bg-primary/10 text-primary" : "text-slate-600"
              )}
            >
              Week
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={() => (view === "day" ? shiftDay(-1) : shiftWeek(-1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[140px] text-center text-sm font-medium text-slate-700">
            {view === "day"
              ? selectedDay.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
              : `${days[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString(undefined, { month: "short", day: "numeric" })}`}
          </span>
          <Button variant="outline" size="sm" onClick={() => (view === "day" ? shiftDay(1) : shiftWeek(1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {!staffLoaded ? null : staff.length === 0 ? (
        <p className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
          Customers can't book you yet: add a staff member (yourself included) under Staff. They start with your opening hours, which you can adjust here.
        </p>
      ) : (
        staffWithoutHours.length > 0 && (
          <p className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
            {staffWithoutHours.map((s) => s.name).join(", ")} {staffWithoutHours.length > 1 ? "have" : "has"} no working hours
            set, so customers see no available times.{" "}
            <button type="button" className="font-semibold underline" onClick={() => setShowAvailability(true)}>
              Set working hours
            </button>
          </p>
        )
      )}
      {pendingCount > 0 && (
        <p className="mb-4 rounded-xl bg-primary/5 p-3 text-sm text-slate-700">
          {pendingCount} booking{pendingCount > 1 ? "s are" : " is"} waiting for you to confirm. Tap a booking to confirm, complete or cancel it.
        </p>
      )}
      {selected && (
        <AppointmentDetails
          a={selected}
          busy={statusBusy}
          error={statusError}
          onStatus={updateStatus}
          onClose={() => setSelectedId(null)}
        />
      )}
      {showAvailability && <AvailabilityPanel staff={staff} onSaved={loadStaff} />}
      {availabilityError && <p className="mb-3 text-sm text-destructive">{availabilityError}</p>}
      {view === "day" && (
        <div className="mb-3">
          <DayAvailability date={selectedDay} staff={staff} busy={availabilityBusy} onChange={setDayAvailable} />
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : view === "week" ? (
        <WeekView days={days} byDay={byDay} holdsByDay={holdsByDay} staff={staff} busy={availabilityBusy} onAvailabilityChange={setDayAvailable} onSelect={select} />
      ) : (
        <DayStaffView date={selectedDay} appointments={byDay(selectedDay)} holds={holdsByDay(selectedDay)} staff={staff} onSelect={select} />
      )}
    </div>
  );
}
